/**
 * branded-docs — pure helpers for the two Phase 0 artifacts that have a Cloudelligent-branded
 * rendering (`preliminary_srs` → DOCX, `kickoff_deck` → PPTX).
 *
 * ── WHY THESE LIVE IN `lib/` AND NOT IN `BrandedDocPane.tsx` ───────────────────────────────────
 * They used to live on the component. `useArtifacts.ts` now needs them too — `useRegenerateBranded`
 * has to answer "does this artifact type even HAVE a branded rendering?" before deciding whether a
 * save/accept should re-run the pipeline — and a hooks module importing a component module to ask a
 * pure question is a cycle waiting to happen (`BrandedDocPane` imports from `useArtifacts`). Pure,
 * dependency-free, importable from either layer.
 */

/** The two branded artifact types. */
export type BrandedArtifactType = 'preliminary_srs' | 'kickoff_deck';

/** The two branded rendering pipelines, as the API's `type` query param spells them. */
export type BrandedPipeline = 'srs' | 'pptx';

export const BRANDED_TYPES: readonly BrandedArtifactType[] = ['preliminary_srs', 'kickoff_deck'];

export function isBrandedType(artifactType: string): artifactType is BrandedArtifactType {
  return BRANDED_TYPES.includes(artifactType as BrandedArtifactType);
}

/** `'srs'` drives the DOCX pipeline; `'pptx'` drives the deck pipeline. */
export function brandedPipelineFor(artifactType: BrandedArtifactType): BrandedPipeline {
  return artifactType === 'preliminary_srs' ? 'srs' : 'pptx';
}

/** Downloaded filename per branded pipeline. */
export const DOWNLOAD_FILENAMES: Record<BrandedPipeline, string> = {
  srs: 'SRS_Document.docx',
  pptx: 'Kickoff_Deck.pptx',
};
